var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useRef } from 'react';
import useAnimationPosition from './hooks/useAnimationPosition';
import useContinuousTap from './hooks/useContinuousTap';
import useDebounceCallback from './hooks/useDebounceCallback';
import useEventListener from './hooks/useEventListener';
import useIsomorphicLayoutEffect from './hooks/useIsomorphicLayoutEffect';
import useMethods from './hooks/useMethods';
import useMountedRef from './hooks/useMountedRef';
import useScrollPosition from './hooks/useScrollPosition';
import useSetState from './hooks/useSetState';
import Photo from './Photo';
import './PhotoBox.css';
import { computePositionEdge, getReachType } from './utils/edgeHandle';
import getMultipleTouchPosition from './utils/getMultipleTouchPosition';
import getPositionOnMoveOrScale from './utils/getPositionOnMoveOrScale';
import getRotateSize from './utils/getRotateSize';
import getSuitableImageSize from './utils/getSuitableImageSize';
import isTouchDevice from './utils/isTouchDevice';
import { limitScale } from './utils/limitTarget';
import { minStartTouchOffset, scaleBuffer } from './variables';
var initialState = {
    // 真实宽度
    naturalWidth: undefined,
    // 真实高度
    naturalHeight: undefined,
    // 宽度
    width: undefined,
    // 高度
    height: undefined,
    // 加载成功状态
    loaded: undefined,
    // 破碎状态
    broken: false,
    // 图片 X 偏移量
    x: 0,
    // 图片 y 偏移量
    y: 0,
    // 图片处于触摸的状态
    touched: false,
    // 背景处于触摸状态
    maskTouched: false,
    // 旋转状态
    rotate: 0,
    // 放大缩小
    scale: 1,
    // 触摸开始时 x 原始坐标
    CX: 0,
    // 触摸开始时 y 原始坐标
    CY: 0,
    // 触摸开始时图片 x 偏移量
    lastX: 0,
    // 触摸开始时图片 y 偏移量
    lastY: 0,
    // 上一个触摸状态 x 原始坐标
    lastCX: 0,
    // 上一个触摸状态 y 原始坐标
    lastCY: 0,
    // 上一个触摸状态的 scale
    lastScale: 1,
    // 触摸开始时时间
    touchTime: 0,
    // 多指触控间距
    touchLength: 0,
    // 是否暂停 transition
    pause: true,
    // 停止 Raf
    stopRaf: true,
    // 当前边缘触发状态
    reach: undefined,
};
export default function PhotoBox(_a) {
    var _b = _a.item, src = _b.src, render = _b.render, _c = _b.width, customWidth = _c === void 0 ? 0 : _c, _d = _b.height, customHeight = _d === void 0 ? 0 : _d, originRef = _b.originRef, visible = _a.visible, speed = _a.speed, easing = _a.easing, wrapClassName = _a.wrapClassName, className = _a.className, style = _a.style, loadingElement = _a.loadingElement, brokenElement = _a.brokenElement, onPhotoTap = _a.onPhotoTap, onMaskTap = _a.onMaskTap, onReachMove = _a.onReachMove, onReachUp = _a.onReachUp, onPhotoResize = _a.onPhotoResize, isActive = _a.isActive, expose = _a.expose;
    var _e = __read(useSetState(initialState), 2), state = _e[0], updateState = _e[1];
    var initialTouchRef = useRef(0);
    var mounted = useMountedRef();
    var _f = state.naturalWidth, naturalWidth = _f === void 0 ? customWidth : _f, _g = state.naturalHeight, naturalHeight = _g === void 0 ? customHeight : _g, _h = state.width, width = _h === void 0 ? customWidth : _h, _j = state.height, height = _j === void 0 ? customHeight : _j, _k = state.loaded, loaded = _k === void 0 ? !src : _k, broken = state.broken, x = state.x, y = state.y, touched = state.touched, stopRaf = state.stopRaf, maskTouched = state.maskTouched, rotate = state.rotate, scale = state.scale, CX = state.CX, CY = state.CY, lastX = state.lastX, lastY = state.lastY, lastCX = state.lastCX, lastCY = state.lastCY, lastScale = state.lastScale, touchTime = state.touchTime, touchLength = state.touchLength, pause = state.pause, reach = state.reach;
    var fn = useMethods({
        onScale: function (current) { return onScale(limitScale(current)); },
        onRotate: function (current) {
            if (rotate !== current) {
                expose({ rotate: current });
                updateState(__assign({ rotate: current }, getSuitableImageSize(naturalWidth, naturalHeight, current)));
            }
        },
    });
    // 默认为屏幕中心缩放
    function onScale(current, clientX, clientY) {
        if (scale !== current) {
            expose({ scale: current });
            updateState(__assign(__assign({ scale: current }, getPositionOnMoveOrScale(x, y, width, height, scale, current, clientX, clientY)), (current <= 1 && { x: 0, y: 0 })));
        }
    }
    var handleMove = useDebounceCallback(function (nextClientX, nextClientY, currentTouchLength) {
        if (currentTouchLength === void 0) { currentTouchLength = 0; }
        if ((touched || maskTouched) && isActive) {
            // 通过旋转调换宽高
            var _a = __read(getRotateSize(rotate, width, height), 2), currentWidth_1 = _a[0], currentHeight_1 = _a[1];
            // 单指最小缩放下，以初始移动距离来判断意图
            if (currentTouchLength === 0 && initialTouchRef.current === 0) {
                var isStillX = Math.abs(nextClientX - CX) <= minStartTouchOffset;
                var isStillY = Math.abs(nextClientY - CY) <= minStartTouchOffset;
                // 初始移动距离不足
                if (isStillX && isStillY) {
                    // 方向记录上次移动距离，以便平滑过渡
                    updateState({ lastCX: nextClientX, lastCY: nextClientY });
                    return;
                }
                // 设置响应状态
                initialTouchRef.current = !isStillX ? 1 : nextClientY > CY ? 3 : 2;
            }
            var offsetX = nextClientX - lastCX;
            var offsetY = nextClientY - lastCY;
            // 边缘触发状态
            var currentReach = undefined;
            if (currentTouchLength === 0) {
                // 边缘超出状态
                var _b = __read(computePositionEdge(offsetX + lastX, scale, currentWidth_1, window.innerWidth), 1), horizontalCloseEdge = _b[0];
                var _c = __read(computePositionEdge(offsetY + lastY, scale, currentHeight_1, window.innerHeight), 1), verticalCloseEdge = _c[0];
                // 边缘触发检测
                currentReach = getReachType(initialTouchRef.current, horizontalCloseEdge, verticalCloseEdge, reach);
                // 接触边缘
                if (currentReach !== undefined) {
                    onReachMove(currentReach, nextClientX, nextClientY, scale);
                }
            }
            // 横向边缘触发、背景触发禁用当前滑动
            if (currentReach === 'x' || maskTouched) {
                updateState({ reach: 'x' });
                return;
            }
            // 目标倍数
            var toScale = limitScale(scale + ((currentTouchLength - touchLength) / 100 / 2) * scale, naturalWidth / width, scaleBuffer);
            // 导出变量
            expose({ scale: toScale });
            updateState(__assign({ touchLength: currentTouchLength, reach: currentReach, scale: toScale }, getPositionOnMoveOrScale(x, y, width, height, scale, toScale, nextClientX, nextClientY, offsetX, offsetY)));
        }
    }, {
        maxWait: 8,
    });
    function updateRaf(position) {
        if (stopRaf || touched) {
            return false;
        }
        if (mounted.current) {
            // 下拉关闭时可以有动画
            updateState(__assign(__assign({}, position), { pause: visible }));
        }
        return mounted.current;
    }
    var slideToPosition = useScrollPosition(function (nextX) { return updateRaf({ x: nextX }); }, function (nextY) { return updateRaf({ y: nextY }); }, function (nextScale) {
        if (mounted.current) {
            expose({ scale: nextScale });
            updateState({ scale: nextScale });
        }
        return !touched && mounted.current;
    });
    var handlePhotoTap = useContinuousTap(onPhotoTap, function (currentClientX, currentClientY) {
        if (!reach) {
            // 若图片足够大，则放大适应的倍数
            var endScale = scale !== 1 ? 1 : Math.max(2, naturalWidth / width);
            onScale(endScale, currentClientX, currentClientY);
        }
    });
    function handleUp(nextClientX, nextClientY) {
        // 重置响应状态
        initialTouchRef.current = 0;
        if ((touched || maskTouched) && isActive) {
            updateState({
                touched: false,
                maskTouched: false,
                pause: false,
                stopRaf: false,
                reach: undefined,
            });
            var safeScale = limitScale(scale, naturalWidth / width);
            // Go
            slideToPosition(x, y, lastX, lastY, width, height, scale, safeScale, lastScale, rotate, touchTime);
            onReachUp(nextClientX, nextClientY);
            // 触发 Tap 事件
            if (CX === nextClientX && CY === nextClientY) {
                if (touched) {
                    handlePhotoTap(nextClientX, nextClientY);
                    return;
                }
                if (maskTouched) {
                    onMaskTap(nextClientX, nextClientY);
                }
            }
        }
    }
    useEventListener(isTouchDevice ? undefined : 'mousemove', function (e) {
        e.preventDefault();
        handleMove(e.clientX, e.clientY);
    });
    useEventListener(isTouchDevice ? undefined : 'mouseup', function (e) {
        handleUp(e.clientX, e.clientY);
    });
    useEventListener(isTouchDevice ? 'touchmove' : undefined, function (e) {
        e.preventDefault();
        var position = getMultipleTouchPosition(e);
        handleMove.apply(void 0, __spreadArray([], __read(position), false));
    }, { passive: false });
    useEventListener(isTouchDevice ? 'touchend' : undefined, function (_a) {
        var changedTouches = _a.changedTouches;
        var touch = changedTouches[0];
        handleUp(touch.clientX, touch.clientY);
    }, { passive: false });
    useEventListener('resize', useDebounceCallback(function () {
        if (loaded && !touched) {
            updateState(getSuitableImageSize(naturalWidth, naturalHeight, rotate));
            onPhotoResize();
        }
    }, { maxWait: 8 }));
    useIsomorphicLayoutEffect(function () {
        if (isActive) {
            expose(__assign({ scale: scale, rotate: rotate }, fn));
        }
    }, [isActive]);
    function handlePhotoLoad(params) {
        updateState(__assign(__assign({}, params), (params.loaded && getSuitableImageSize(params.naturalWidth || 0, params.naturalHeight || 0, rotate))));
    }
    function handleStart(currentClientX, currentClientY, currentTouchLength) {
        if (currentTouchLength === void 0) { currentTouchLength = 0; }
        updateState({
            touched: true,
            CX: currentClientX,
            CY: currentClientY,
            lastCX: currentClientX,
            lastCY: currentClientY,
            lastX: x,
            lastY: y,
            lastScale: scale,
            touchLength: currentTouchLength,
            touchTime: Date.now(),
        });
    }
    function handleWheel(e) {
        if (!reach) {
            // 限制最大倍数和最小倍数
            var toScale = limitScale(scale - e.deltaY / 100 / 2, naturalWidth / width);
            updateState({ stopRaf: true });
            onScale(toScale, e.clientX, e.clientY);
        }
    }
    function handleMaskStart(e) {
        updateState({
            maskTouched: true,
            CX: e.clientX,
            CY: e.clientY,
            lastX: x,
            lastY: y,
        });
    }
    function handleTouchStart(e) {
        e.stopPropagation();
        handleStart.apply(void 0, __spreadArray([], __read(getMultipleTouchPosition(e)), false));
    }
    function handleMouseDown(e) {
        e.stopPropagation();
        if (e.button === 0) {
            handleStart(e.clientX, e.clientY, 0);
        }
    }
    // 计算位置
    var _l = __read(useAnimationPosition(visible, originRef, loaded, x, y, width, height, scale, speed, function (isPause) { return updateState({ pause: isPause }); }), 8), translateX = _l[0], translateY = _l[1], currentWidth = _l[2], currentHeight = _l[3], currentScale = _l[4], opacity = _l[5], easingMode = _l[6], FIT = _l[7];
    // 图片 objectFit 渐变时间
    var transitionLayoutTime = easingMode < 4 ? speed / 2 : easingMode > 4 ? speed : 0;
    var transitionCSS = "transform ".concat(speed, "ms ").concat(easing);
    var attrs = {
        className: className,
        onMouseDown: isTouchDevice ? undefined : handleMouseDown,
        onTouchStart: isTouchDevice ? handleTouchStart : undefined,
        onWheel: handleWheel,
        style: {
            width: currentWidth,
            height: currentHeight,
            opacity: opacity,
            objectFit: easingMode === 4 ? undefined : FIT,
            transform: rotate ? "rotate(".concat(rotate, "deg)") : undefined,
            transition: 
            // 初始状态无渐变
            easingMode > 2 ? "".concat(transitionCSS, ", opacity ").concat(speed, "ms ease, height ").concat(transitionLayoutTime, "ms ").concat(easing) : undefined,
        },
    };
    return (_jsx("div", __assign({ className: "PhotoView__PhotoWrap".concat(wrapClassName ? " ".concat(wrapClassName) : ''), style: style, onMouseDown: !isTouchDevice && isActive ? handleMaskStart : undefined, onTouchStart: isTouchDevice && isActive ? function (e) { return handleMaskStart(e.touches[0]); } : undefined }, { children: _jsx("div", __assign({ className: "PhotoView__PhotoBox", style: {
                transform: "matrix(".concat(currentScale, ", 0, 0, ").concat(currentScale, ", ").concat(translateX, ", ").concat(translateY - 107, ")"),
                transition: touched || pause ? undefined : transitionCSS,
                willChange: isActive ? 'transform' : undefined,
            } }, { children: src ? (_jsx(Photo, __assign({ src: src, loaded: loaded, broken: broken }, attrs, { onPhotoLoad: handlePhotoLoad, loadingElement: loadingElement, brokenElement: brokenElement }))) : (render && render({ attrs: attrs, scale: currentScale, rotate: rotate })) })) })));
}
